import {
	ApolloClient,
	ApolloLink,
	InMemoryCache,
	createHttpLink,
	split,
	Observable
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient, type Client } from 'graphql-ws';
import {
	GRAPHQL_DEFAULT_HEADERS
} from '@/providers/graphql/headers';

export const API_URL = import.meta.env.VITE_API_URL;

// Auth error callback - will be set by AuthContext
let onAuthError: (() => void) | null = null;

export const setAuthErrorCallback = (callback: () => void) => {
	onAuthError = callback;
};

export const clearAuthErrorCallback = () => {
	onAuthError = null;
};

// Derive WebSocket URL from API URL
const getWsUrl = (httpUrl: string): string => {
	const url = new URL(httpUrl);
	url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
	return url.toString();
};

export const WS_URL = getWsUrl(API_URL);

const httpLink = createHttpLink({
	uri: API_URL,
	headers: GRAPHQL_DEFAULT_HEADERS,
	credentials: 'include' // Enable cookies for authentication
});

// Create WebSocket link for subscriptions
// The client is created lazily and can be reconnected when auth state changes
let wsClient: Client | null = null;
let wsLink: GraphQLWsLink | null = null;

export const createWsClient = () => {
	if (wsClient) {
		wsClient.dispose();
	}

	wsClient = createClient({
		url: WS_URL,
		connectionParams: () => {
			// Cookies are automatically sent with WebSocket connection
			return {};
		},
		retryAttempts: 5,
		shouldRetry: () => true,
		on: {
			connected: () => console.log('[WS] Connected to GraphQL subscriptions'),
			closed: () => console.log('[WS] Disconnected from GraphQL subscriptions'),
			error: (error) =>
				console.error('[WS] GraphQL subscription error:', error)
		}
	});

	wsLink = new GraphQLWsLink(wsClient);
	return wsClient;
};

export const disposeWsClient = () => {
	if (wsClient) {
		wsClient.dispose();
		wsClient = null;
		wsLink = null;
	}
};

export const getWsLink = (): GraphQLWsLink => {
	if (!wsLink) {
		createWsClient();
	}
	return wsLink!;
};

// Create a lazy link that delegates to the WebSocket link
// This allows us to create/dispose the WS connection based on auth state
const lazyWsLink = new ApolloLink((operation) => {
	return new Observable((observer) => {
		const link = getWsLink();
		const subscription = link.request(operation)?.subscribe({
			next: observer.next.bind(observer),
			error: observer.error.bind(observer),
			complete: observer.complete.bind(observer)
		});

		return () => {
			subscription?.unsubscribe();
		};
	});
});

// Error link to handle authentication errors
const errorLink = onError(({ graphQLErrors, networkError }) => {
	if (graphQLErrors) {
		for (const error of graphQLErrors) {
			// Check for authentication error code in extensions
			const errorCode = (error.extensions as { code?: string })?.code;
			if (
				errorCode === 'AUTH_NOT_AUTHENTICATED' ||
				errorCode === 'UNAUTHENTICATED'
			) {
				console.warn('[Auth] Received authentication error, triggering logout');
				if (onAuthError) {
					onAuthError();
				}
				break;
			}
		}
	}

	// Also handle network-level 401 errors
	if (networkError && 'statusCode' in networkError) {
		const statusCode = (networkError as { statusCode?: number }).statusCode;
		if (statusCode === 401) {
			console.warn('[Auth] Received 401 network error, triggering logout');
			if (onAuthError) {
				onAuthError();
			}
		}
	}
});

// Split link: send subscriptions to WebSocket, queries/mutations to HTTP
const splitLink = split(
	({ query }) => {
		const definition = getMainDefinition(query);
		return (
			definition.kind === 'OperationDefinition' &&
			definition.operation === 'subscription'
		);
	},
	lazyWsLink,
	httpLink
);

// Combine error link with split link
const link = ApolloLink.from([errorLink, splitLink]);

export const apolloClient = new ApolloClient({
	link: link,
	cache: new InMemoryCache({
		// Configure cache behavior
		addTypename: true,
		resultCaching: true
	}),
	// Default fetch policy for all queries
	defaultOptions: {
		query: {
			fetchPolicy: 'cache-first', // Use cache first, then network
			errorPolicy: 'all', // Return both data and errors
			notifyOnNetworkStatusChange: true // Update loading state on network changes
		},
		mutate: {
			errorPolicy: 'all'
		},
		watchQuery: {
			fetchPolicy: 'cache-and-network', // Use cache first, then network
			errorPolicy: 'all',
			notifyOnNetworkStatusChange: true
		}
	}
});
